import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { LocalDate } from 'src/utilities/local-date.js';
import { numberWord } from 'src/utilities/text.js';
import { DListSection } from './d-list-section.js';
import type { ListItemIcon } from './listItemIcon';
import type { ListSectionItemInput } from './listSectionItemInput';
import type { TutorialListItem } from './tutorial-list-item';

/**
 *
 */
@customElement('d-list-section-tutorials')
export class DListSectionTutorials extends LitElement {
  static readonly styles = [
    ...DListSection.styles,
    css`
      :host {
        display: block;
      }
    `,
  ];
  @property({ type: String })
  label = t('library.tutorials.coursesAndGuides');
  @property({ type: Boolean, attribute: 'theme-page', reflect: true })
  themePage = false;
  @property({ type: Array })
  tutorials: TutorialListItem[] = [];
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: String })
  today = LocalDate.now().toString();

  private get tutorialItems(): ListSectionItemInput[] {
    return this.tutorials.map((tutorial) => {
      let mainIcon: ListItemIcon | undefined = undefined;
      let secondaryLabel = t('library.tutorials.registeredOn', {
        date: LocalDate.fromString(tutorial.assigned).toStringForDisplay(),
      });
      if (tutorial.completed) {
        const completedDate = LocalDate.fromString(tutorial.completed);
        mainIcon = 'checkGray';
        secondaryLabel = t('library.tutorials.completedOn', { date: completedDate.toStringForDisplay() });
      } else if (tutorial.due) {
        const now = LocalDate.fromString(this.today);
        const dueDate = LocalDate.fromString(tutorial.due);
        if (dueDate.isBefore(now)) {
          mainIcon = 'alert';
          const daysOverdue = dueDate.until(now);
          const overdueSuffix =
            daysOverdue === 1
              ? t('library.tutorials.overdueSuffix_one', { count: numberWord(dueDate.until(now)) })
              : t('library.tutorials.overdueSuffix_other', { count: numberWord(dueDate.until(now)) });
          secondaryLabel += overdueSuffix;
        } else {
          secondaryLabel += t('library.tutorials.dueDateSuffix', { date: dueDate.toStringForDisplay() });
        }
      }
      return {
        label: tutorial.name,
        secondaryLabel: secondaryLabel,
        clickData: tutorial.uuid,
        accessible: true,
        mainIcon,
      };
    });
  }

  render() {
    return html`
      <d-list-section
        .label=${this.label}
        .themePage=${this.themePage}
        icon="tutorials"
        .items=${this.tutorialItems}
        .contentStickyTop=${this.contentStickyTop}
      >
      </d-list-section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-list-section-tutorials': DListSectionTutorials;
  }
}
